import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import { errorCodeSelector, optionsSelector, templateSelector, templatesSelector } from './store/selector';
import {
  getTemplatesFailureApiAction,
  getTemplatesInitApiAction,
  getTemplatesSuccessApiAction,
  postTemplateFailuireApiAction,
  postTemplateInitApiAction,
  postTemplateSuccessApiAction,
  putTemplateFailuireApiAction,
  putTemplateInitApiAction,
  putTemplateSuccessApiAction,
  resetOptionsAction,
  setOptionsAction,
  setTemplateAction,
  templateClearAction
} from './store/reducer';
import { PostTemplatePayloadRequest } from './interfaces/post-template';
import { templateApiService } from './services/api';
import { PutTemplatePayloadRequest } from './interfaces/put-template';
import { GetTemplatesParams } from './interfaces/get-templates';
import { Template } from '../../interfaces/template.interface';

export function useTemplateCore() {
  const templates = useSelector(templatesSelector);
  const template = useSelector(templateSelector);
  const options = useSelector(optionsSelector);
  const errorCode = useSelector(errorCodeSelector);
  const dispatch = useDispatch();

  const getTemplates = useCallback(
    async (onSuccess?: () => void, onFailure?: () => void) => {
      try {
        dispatch(getTemplatesInitApiAction());
        const response = await templateApiService.getTemplates();
        dispatch(getTemplatesSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(getTemplatesFailureApiAction({ errorCode: error.request.status }));
      }
    },
    [dispatch]
  );

  const postTemplate = useCallback(
    async (body: PostTemplatePayloadRequest, onSuccess?: (id: string) => void, onFailure?: () => void) => {
      try {
        dispatch(postTemplateInitApiAction());
        const response = await templateApiService.postTemplate(body);
        dispatch(postTemplateSuccessApiAction());
        onSuccess?.(response.id);
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(postTemplateFailuireApiAction());
      }
    },
    [dispatch]
  );

  const putTemplate = useCallback(
    async (
      id: string,
      body: PutTemplatePayloadRequest,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(putTemplateInitApiAction());
        await templateApiService.putTemplate(id, body);
        dispatch(putTemplateSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(putTemplateFailuireApiAction());
      }
    },
    [dispatch]
  );

  const selectTemplate = useCallback((template: Template) => {
    dispatch(setTemplateAction(template));
  }, [dispatch]);

  const setOptions = useCallback((options: GetTemplatesParams) => {
    dispatch(setOptionsAction(options));
  }, [dispatch]);

  const resetOptions = useCallback(() => {
    dispatch(resetOptionsAction());
  }, [dispatch]);

  const clearTemplate = useCallback(() => {
    dispatch(templateClearAction());
  }, [dispatch]);

  return {
    templates,
    template,
    options,
    errorCode,
    getTemplates,
    postTemplate,
    putTemplate,
    selectTemplate,
    setOptions,
    resetOptions,
    clearTemplate,
  };
}
